import type { NextApiRequest, NextPageContext } from 'next/types';
import { ComponentType, SyntheticEvent } from 'react';
import { InferableComponentEnhancerWithProps } from 'react-redux';
import { AnyAction, Dispatch, Store } from 'redux';
import { ActionsObservable, StateObservable } from 'redux-observable';
import { Observable } from 'rxjs';
import { Weddings } from '@bridebook/models';
import { ValidationError } from '@bridebook/toolbox/src';
import type { IAbTestingState } from '@bridebook/toolbox/src/ab-testing/lib/types';
import type { Action, IUISupplier, Slug, UrlFriendlySlug } from '@bridebook/toolbox/src/types';
import { QueryClient } from '@tanstack/react-query';
import type { ISearchPageQuery } from '@bridebook/web/app-shared/lib/search/types';
import { AutocompletePredictionsService } from '@bridebook/web/app-shared/lib/search/utils/autocomplete-predictions-service';
import { GooglePlacesService } from '@bridebook/web/app-shared/lib/search/utils/google-places-service';
import { TQuizId } from '@bridebook/web/components/quiz/types';
import { ICollaborationState } from '@bridebook/web/lib/access-control';
import type { AIPlannerState } from '@bridebook/web/lib/ai-planner/slice';
import { AppState } from '@bridebook/web/lib/app/types';
import { IArticlesState } from '@bridebook/web/lib/articles/types';
import {
  CustomAuthErrorCodes,
  FirebaseErrorCodes,
  IAuthState,
} from '@bridebook/web/lib/auth/types';
import { DatePickerType } from '@bridebook/web/lib/datepicker/types';
import { IEnquiriesState } from '@bridebook/web/lib/enquiries/types';
import { FeatureCardsService } from '@bridebook/web/lib/feature-cards/feature-cards-service';
import { IGuestlistState } from '@bridebook/web/lib/guestlist/types';
import { IModulesState } from '@bridebook/web/lib/modules/types';
import { IScrapbookState } from '@bridebook/web/lib/scrapbook/types';
import { SettingsState } from '@bridebook/web/lib/settings/types';
import { ISupplierState } from '@bridebook/web/lib/supplier/types';
import { CordovaTrackerService } from '@bridebook/web/lib/track-utils/services/cordova-tracker-service';
import { IUsersState } from '@bridebook/web/lib/users/types';
import { MuteActionsService } from '@bridebook/web/lib/utils/mute-actions-service';
import { WarningModalService } from '@bridebook/web/lib/utils/warning-modal-service';
import validate from '@bridebook/web/lib/validate';
import { IVenuerexState } from '@bridebook/web/lib/venuerex/types';
import { NextPageContextWithBridebookExtras } from './auth/session';
import { IBBCommonState } from './bbcommon/types';
import { IBudgetState } from './budget/types';
import { IChecklistState } from './checklist/types';
import { IFirebaseDeps } from './configure-deps';
import { EpicsState } from './epics/types';
import { ImageInspirationState } from './image-inspiration/types';
import { MobileAppState } from './mobile-app/types';
import { ISearchState } from './search/types';
import { IShortlistState } from './shortlist/types';
import { TaskState } from './task/types';
import { UiState } from './ui/types';
import { IVenueConfirmState } from './venue-confirmation/types';
import { IWeddingsState } from './weddings/types';
import { IWeddingEventsState } from '@bridebook/web/lib/events/types';
import { LostLeadService } from '@bridebook/web/lib/lost-lead/lost-lead-service';
import { ShowroundModalState } from '@bridebook/web/lib/showrounds/types/types';
import type { IronSession } from 'iron-session';

export type { Action, ActionWithPayload, RecursivePartial } from '@bridebook/toolbox/src/types';

// General re-export
export * from '@bridebook/web/lib/app/types';
export * from '@bridebook/web/lib/mobile-app/types';
export * from '@bridebook/web/lib/articles/types';
export * from '@bridebook/web/lib/auth/types';
export * from '@bridebook/web/lib/bbcommon/types';
export * from '@bridebook/web/lib/budget/types';
export * from '@bridebook/web/lib/checklist/types';
export * from '@bridebook/web/lib/datepicker/types';
export * from '@bridebook/web/lib/enquiries/types';
export * from '@bridebook/web/lib/i18n/types';
export * from '@bridebook/web/lib/guestlist/types';
export * from '@bridebook/web/lib/weddings/types';
export * from '@bridebook/web/lib/search/types';
export * from '@bridebook/web/lib/settings/types';
export * from '@bridebook/web/lib/shortlist/types';
export * from '@bridebook/web/lib/epics/types';
export * from '@bridebook/web/lib/supplier/types';
export * from '@bridebook/web/lib/ui/types';
export * from '@bridebook/web/lib/users/types';
export * from '@bridebook/web/lib/venuerex/types';
export * from '@bridebook/web/lib/venue-confirmation/types';
export * from '@bridebook/web/lib/scrapbook/types';

// core types

export type ValueOf<T> = T[keyof T];

// These params will be added to state, as well as used in Cordova Universal Links
export const ALLOWED_CUSTOM_PARAMS = [
  'next',
  'searchPopup',
  'enquiryConfirmation',
  'enquirySupplier',
  'conversationId',
  'venueConfirmationPopup',
  'slug',
  'area',
  'id',
  'nonce',
  'resetemail',
  'page',
  'category',
  'searchParams',
  'shortlistedLocation',
  'skipOnboarding',
  'calendar',
  'reconfirm',
  'directReconfirm',
  'feedback',
  'cancel',
  'schedule',
  'event',
  'countryCode',
  'viewedLocation',
] as const;

export type Query = Partial<
  Record<(typeof ALLOWED_CUSTOM_PARAMS)[number], string> & {
    slug: Slug | UrlFriendlySlug;
    market: string;
  }
>;

export interface NameValue {
  name: string;
  value: string;
}

type ExtendedError = Error & { code?: FirebaseErrorCodes | CustomAuthErrorCodes; prop: string };

export type BridebookError = ValidationError | ExtendedError;

export interface IApplicationState {
  app: AppState;
  abTesting: IAbTestingState;
  aiPlanner: AIPlannerState;
  auth: IAuthState;
  articles: IArticlesState;
  checklist: IChecklistState;
  shortlist: IShortlistState;
  search: ISearchState;
  budget: IBudgetState;
  guestlist: IGuestlistState;
  supplier: ISupplierState;
  ui: UiState;
  users: IUsersState;
  settings: SettingsState;
  onboarding: OnboardingState;
  datepicker: DatePickerType;
  enquiries: IEnquiriesState;
  weddings: IWeddingsState;
  bbcommon: IBBCommonState;
  venueConfirmation: IVenueConfirmState;
  scrapbook: IScrapbookState;
  epics: EpicsState;
  mobileapp: MobileAppState;
  venuerex: IVenuerexState;
  modules: IModulesState;
  acl: ICollaborationState;
  quiz: QuizState;
  task: TaskState;
  imageInspiration: ImageInspirationState;
  weddingEvents: IWeddingEventsState;
  showround: ShowroundModalState;
}

// Actions

export interface Entities {
  wedding: () => Weddings;
}

export interface IBasicDeps extends IFirebaseDeps {
  getState: () => IApplicationState;
  now: () => number;
  validate: typeof validate;
  cordovaTracker: CordovaTrackerService;
  muteActions: MuteActionsService;
  warningModal: WarningModalService;
  googlePlacesService: GooglePlacesService;
  autocompletePredictionsService: AutocompletePredictionsService;
  featureCardsService: FeatureCardsService;
  queryClient: QueryClient;
  lostLeadService: LostLeadService;
}

export interface IDeps extends IBasicDeps, Entities, IConnectedDispatch {}

export type EpicEntity = Observable<Entities[keyof Entities]>;

export interface EpicEntities {
  wedding$: EpicEntity;
}

export interface IEpicDeps extends IBasicDeps, EpicEntities {
  state$: StateObservable<IApplicationState>;
}

export interface IQuery extends Record<string, any> {}

export type UrlQuery<T> = T extends ISearchPageQuery ? ISearchPageQuery : IQuery;

export interface IUrl<T = IQuery> {
  pathname?: string;
  query: UrlQuery<T>;
  slug?: UrlFriendlySlug;
  searchParams?: string[];
  area?: string;
  placeId?: string;
}

export interface CustomDispatch<T> extends Dispatch<Action> {
  (t: T): void;
}

export type ActionCreatorWithDeps<A extends Action> = (...args: any[]) => (deps: IDeps) => A;

export interface IConnectedDispatch<A extends Action = Action> {
  dispatch: CustomDispatch<A | ReturnType<ActionCreatorWithDeps<A>>>;
}

export type AppStore = Store<IApplicationState> & {
  dispatch: IConnectedDispatch['dispatch'];
};

/**
 * The dispatch seam of a domain hook whose flow dispatches on its own: a
 * caller acting on someone else's behalf (the AI planner) passes its own
 * marking dispatch, so the flow's follow-ups carry that caller's attribution.
 * Without it the hook stays on the store dispatch — the couple's.
 */
export type ActorDispatchOverride = { dispatch?: AppStore['dispatch'] };

export interface IOnClick<T extends HTMLElement = HTMLButtonElement> {
  onClick?: (e?: SyntheticEvent<T, Event>) => void;
}

export interface IIntlMessageDescriptor {
  id: string;
  defaultMessage: string;
  description?: string | Record<string, any>;
}

export type IReducersImmer<T> = (draft: T) => Record<string, (action?: any) => T | void>;

export type ConnectedTypes<T> =
  T extends InferableComponentEnhancerWithProps<infer Props, infer _>
    ? Omit<Props, 'dispatch'> & { dispatch: IDeps['dispatch'] }
    : never;

interface IMarker {
  id: string;
  lat: number;
  lng: number;
}

export type IUISupplierWithMarker = IUISupplier & IMarker;

// Allows to resolve return type of a promise
export type Await<T> = T extends PromiseLike<infer U> ? U : T;

// Allows to mark some properties as optional
export type WithOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/* ############################################################################
 *  Next.js specific props
 * ######################################################################### */
/**
 * Passed as an argument to getInitialProps
 */
export type PageInitialProps = {
  ctx: NextPageContextWithBridebookExtras;
  store: Store<IApplicationState>;
  user?: string | null;
};

/**
 * Passed as an argument to page-wrapping HOCs
 */
export type CustomPage = ComponentType<any> & {
  getInitialProps: (props: PageInitialProps) => Record<string, any> | void;
};

/* ############################################################################
 *  DEPRECATED START
 * ######################################################################### */
/**
 * @deprecated
 * Use storeEnhancer approach with ConnectedProps from react-redux instead
 */
export type IConnectedProps<T extends (...args: any) => any> = ReturnType<T> & IConnectedDispatch;

/**
 * @deprecated
 * Use IConnectedDispatch instead
 */
export type ConnectedDispatch<T extends Action = any> = IConnectedDispatch<T>;

/**
 * @deprecated
 * Use IApplicationState instead
 */
export type State = IApplicationState;

/* ############################################################################
 *  DEPRECATED END
 * ######################################################################### */

export interface InputTarget<N = string> extends EventTarget {
  name: N;
  value: string;
}

export interface InputEvent extends Event {
  target: InputTarget;
  currentTarget: InputTarget;
}

interface WebSession extends IronSession<Record<string, unknown>> {
  user_id?: string;
  provider_id?: string;
}

interface WebReq extends NextApiRequest {
  device?: { sessionId: string; isCordova: boolean };
  session?: WebSession;
  i18n?: {
    userId: string;
    baseUrl: string;
    supportedLanguages: string[];
  };
}

export interface WebPageContext extends NextPageContext {
  req: WebReq;
}

export type IEpic<Input extends AnyAction = AnyAction, Output extends AnyAction = AnyAction> = (
  action$: ActionsObservable<Input>,
  deps: IEpicDeps,
) => Observable<Output>;

export interface OnboardingState {
  suppliersToContact: IUISupplier[];
}

export interface QuizState {
  modal:
    | { open: true; quizId: TQuizId; quizSource: string; showIntroPage?: boolean }
    | { open: false; quizId?: TQuizId };
  quizCompleted: Record<string, boolean>;
  interceptSearchParams: string | null;
}

export type ReturnTypeAction<Type, Func extends (...args: any) => any> = Omit<
  ReturnType<Func>,
  'type'
> & {
  type: Type;
};

export enum SNAP_INDEX {
  CLOSED = 0,
  HALF = 1,
  OPENED = 2,
}
